/**
 * Sprint 24.15 — HireWizardTemplateGallery (step 2, template path).
 *
 * The catalogue: a featured Kuvio Architect card + a 2×2 grid of the rest, with
 * category filter chips. Per the Sprint 24.15 visual contract (featured = 2px
 * info border; category-tinted icon containers; loading/empty/error states).
 */

import { useMemo, useState, type CSSProperties } from "react";
import {
  useListAgentTemplates,
  type AgentTemplateSummary,
} from "../../hooks/use-agent-templates";
import { TemplateIcon } from "./template-visuals";

export interface HireWizardTemplateGalleryProps {
  onPreview: (templateId: string) => void;
  onHire: (templateId: string) => void;
}

const FEATURED_ID = "tpl_kuvio_architect";

export function HireWizardTemplateGallery({ onPreview, onHire }: HireWizardTemplateGalleryProps) {
  const { data: templates, isLoading, isError, refetch } = useListAgentTemplates();
  const [filter, setFilter] = useState<string>("all");

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const t of templates ?? []) set.add(t.category);
    return ["all", ...[...set].sort()];
  }, [templates]);

  const filtered = useMemo(
    () => (templates ?? []).filter((t) => filter === "all" || t.category === filter),
    [templates, filter],
  );

  const featured = filtered.find((t) => t.id === FEATURED_ID) ?? null;
  const rest = filtered.filter((t) => t.id !== featured?.id);

  return (
    <div style={body}>
      <header style={headerWrap}>
        <h3 style={h3}>Hire from a template</h3>
        <div style={subtitle}>Proven agents, ready to customise and hire.</div>
      </header>

      {/* Filter chips */}
      {!isLoading && !isError && (templates?.length ?? 0) > 0 && (
        <div style={chipRow}>
          {categories.map((c) => {
            const active = filter === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setFilter(c)}
                style={{
                  ...filterChip,
                  ...(active ? filterChipActive : filterChipInactive),
                }}
              >
                {c === "all" ? "All" : capitalise(c)}
              </button>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <GallerySkeleton />
      ) : isError ? (
        <div style={stateCard}>
          <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Couldn't load templates.</span>
          <button type="button" onClick={() => void refetch()} style={retryBtn}>
            <i className="ti ti-refresh" aria-hidden="true" /> Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={stateCard}>
          <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No templates available yet.</span>
        </div>
      ) : (
        <>
          {featured && <FeaturedCard template={featured} onPreview={onPreview} onHire={onHire} />}
          {rest.length > 0 && (
            <div style={grid}>
              {rest.map((t) => (
                <CompactCard key={t.id} template={t} onPreview={onPreview} onHire={onHire} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FeaturedCard({
  template,
  onPreview,
  onHire,
}: {
  template: AgentTemplateSummary;
  onPreview: (id: string) => void;
  onHire: (id: string) => void;
}) {
  return (
    <div style={featuredCard}>
      <div style={{ display: "flex", gap: 14 }}>
        <TemplateIcon icon={template.icon} ramp={template.ramp} size={52} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={cardName}>{template.name}</span>
            <span style={firstPartyPill}>First-party</span>
          </div>
          <div style={cardDesc}>{template.short_description ?? template.persona}</div>
          <div style={metaRow}>
            <span style={metaSpan}>
              <i className="ti ti-plug-connected" aria-hidden="true" /> {template.default_equipment.length} connections
            </span>
            <span style={metaSpan}>
              <i className="ti ti-repeat" aria-hidden="true" /> {template.starter_routines.length} routines
            </span>
            <span style={metaSpan}>
              <i className="ti ti-flame" aria-hidden="true" /> {template.starter_priorities.length} tasks
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={() => onPreview(template.id)} style={secondaryBtn}>
          Preview
        </button>
        <button type="button" onClick={() => onHire(template.id)} style={primaryBtn}>
          Hire {template.name.split(" ").slice(-1)[0] ?? template.name} <i className="ti ti-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function CompactCard({
  template,
  onPreview,
  onHire,
}: {
  template: AgentTemplateSummary;
  onPreview: (id: string) => void;
  onHire: (id: string) => void;
}) {
  return (
    <div style={compactCard}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <TemplateIcon icon={template.icon} ramp={template.ramp} size={40} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={cardName}>{template.name}</div>
          <div style={{ ...cardDesc, marginTop: 2 }}>{template.short_description ?? template.persona}</div>
        </div>
      </div>
      <div style={equipCounter}>
        <i className="ti ti-plug-connected" aria-hidden="true" /> {template.default_equipment.length} connections
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={() => onPreview(template.id)} style={{ ...secondaryBtn, flex: 1, padding: "6px 10px" }}>
          Preview
        </button>
        <button type="button" onClick={() => onHire(template.id)} style={{ ...primaryBtn, flex: 1, padding: "6px 10px" }}>
          Hire
        </button>
      </div>
    </div>
  );
}

function GallerySkeleton() {
  return (
    <>
      <div style={{ ...featuredCard, opacity: 0.6 }}>
        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ ...skel, width: 52, height: 52, borderRadius: "var(--border-radius-md)" }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ ...skel, width: 160, height: 14 }} />
            <div style={{ ...skel, width: "80%", height: 10 }} />
            <div style={{ ...skel, width: "50%", height: 10 }} />
          </div>
        </div>
      </div>
      <div style={grid}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ ...compactCard, opacity: 0.6 }}>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ ...skel, width: 40, height: 40, borderRadius: "var(--border-radius-md)" }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ ...skel, width: 100, height: 12 }} />
                <div style={{ ...skel, width: "90%", height: 9 }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function capitalise(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

const body: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  padding: "16px 20px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
const headerWrap: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const h3: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 500, color: "var(--color-text-primary)" };
const subtitle: CSSProperties = { fontSize: 14, color: "var(--color-text-secondary)" };

const chipRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 8 };
const filterChip: CSSProperties = {
  fontSize: 12,
  padding: "4px 12px",
  borderRadius: 999,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const filterChipActive: CSSProperties = {
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  border: "0.5px solid var(--color-text-primary)",
};
const filterChipInactive: CSSProperties = {
  background: "transparent",
  color: "var(--color-text-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
};

const featuredCard: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  border: "2px solid var(--color-border-info)",
  borderRadius: "var(--border-radius-lg)",
  padding: "1.25rem 1.5rem",
  background: "var(--color-background-primary)",
};
const grid: CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const compactCard: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  padding: 14,
  background: "var(--color-background-primary)",
};

const cardName: CSSProperties = { fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" };
const cardDesc: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.45,
  color: "var(--color-text-secondary)",
  marginTop: 4,
};
const firstPartyPill: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-info)",
  color: "var(--color-text-info)",
  flexShrink: 0,
};
const metaRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 14, marginTop: 8 };
const metaSpan: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 12,
  color: "var(--color-text-tertiary)",
};
const equipCounter: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  marginTop: "auto",
};

const secondaryBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 14px",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "transparent",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const primaryBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 14px",
  borderRadius: "var(--border-radius-md)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const stateCard: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  padding: 32,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
};
const retryBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const skel: CSSProperties = { background: "var(--color-background-tertiary)", borderRadius: 4 };
